<template>
  <div class="banner-table-container">
    <!-- 테이블 상단 (BannerTable.vue 구조) -->
    <div class="table-header">
      <div class="table-info">
        <span class="total-count">
          총 <strong>{{ rows.length }}</strong>개의 광고 병원
        </span>
      </div>
      <div class="table-actions">
        <a-button type="primary" class="yellow-button" @click="$emit('add')">
          <i class="fa-solid fa-plus"></i>
          광고 병원 추가
        </a-button>
      </div>
    </div>

    <!-- 1200px 이상: 표 -->
    <div class="draggable-table-container featured-table-only">
      <div class="table-header-row">
        <div class="table-cell index-cell">번호</div>
        <div class="table-cell name-cell">병원명</div>
        <div class="table-cell period-cell">광고 기간</div>
        <div class="table-cell status-cell">상태</div>
        <div class="table-cell visible-cell">게시 여부</div>
        <div class="table-cell date-cell">등록일</div>
        <div class="table-cell action-cell">관리</div>
      </div>

      <div v-if="loading" class="loading-container">
        <div class="loading-spinner">
          <i class="fa-solid fa-spinner fa-spin"></i>
          <span>데이터를 불러오는 중입니다...</span>
        </div>
      </div>
      <div v-else-if="rows.length === 0" class="no-data">
        <span>등록된 광고 병원이 없습니다.</span>
      </div>
      <div v-for="(row, index) in loading ? [] : rows" :key="row.companyId" class="table-row">
        <div class="table-cell index-cell">{{ index + 1 }}</div>
        <div class="table-cell name-cell">
          <div class="banner-name">
            <span class="name-text">{{ row.name }}</span>
            <span class="hospital-region">{{ row.region }}</span>
          </div>
        </div>
        <div class="table-cell period-cell">
          <div class="display-period">
            <div class="period-item">
              <span class="period-label">시작:</span>
              <span class="period-date">{{ row.startDate }}</span>
            </div>
            <div class="period-item">
              <span class="period-label">마감:</span>
              <span class="period-date">{{ row.endDate || '마감일 없음' }}</span>
            </div>
          </div>
        </div>
        <div class="table-cell status-cell">
          <a-tag :color="statusOf(row).color">{{ statusOf(row).text }}</a-tag>
        </div>
        <div class="table-cell visible-cell">
          <div class="display-status">
            <a-switch :checked="row.isFeatured" @change="$emit('toggle', row)" />
            <span :class="['status-text', row.isFeatured ? 'active' : 'inactive']">
              {{ row.isFeatured ? '게시' : '미게시' }}
            </span>
          </div>
        </div>
        <div class="table-cell date-cell">
          <div class="created-date">{{ formatDate(row.registeredAt) }}</div>
        </div>
        <div class="table-cell action-cell">
          <div class="action-buttons">
            <a-button size="small" type="primary" class="yellow-button" @click="$emit('edit', row)">
              <i class="fa-solid fa-edit"></i>
              수정
            </a-button>
            <a-button size="small" danger @click="$emit('remove', row)">
              <i class="fa-solid fa-trash"></i>
              삭제
            </a-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 1199px 이하: 카드 (ParticipantMonitoring.vue 모바일 카드 구조) -->
    <div class="featured-mobile-list featured-cards-only">
      <a-spin :spinning="loading">
        <div v-if="rows.length === 0" class="mobile-empty">
          <a-empty description="등록된 광고 병원이 없습니다." />
        </div>
        <div v-else class="mobile-cards-container">
          <div v-for="(row, index) in rows" :key="row.companyId" class="featured-card">
            <div class="card-header">
              <span class="card-index">{{ index + 1 }}</span>
              <span class="card-title">{{ row.name }}</span>
              <div class="card-segment">
                <a-tag :color="statusOf(row).color">{{ statusOf(row).text }}</a-tag>
              </div>
            </div>
            <div class="card-body">
              <div class="card-row">
                <span class="card-label">지역</span>
                <span class="card-value">{{ row.region || '-' }}</span>
              </div>
              <div class="card-row">
                <span class="card-label">광고 기간</span>
                <span class="card-value">{{ row.startDate }} ~ {{ row.endDate || '마감일 없음' }}</span>
              </div>
              <div class="card-row">
                <span class="card-label">게시 여부</span>
                <span class="card-value">
                  <a-switch size="small" :checked="row.isFeatured" @change="$emit('toggle', row)" />
                  {{ row.isFeatured ? '게시' : '미게시' }}
                </span>
              </div>
              <div class="card-row">
                <span class="card-label">등록일</span>
                <span class="card-value">{{ formatDate(row.registeredAt) }}</span>
              </div>
            </div>
            <div class="card-footer">
              <a-button size="small" type="primary" class="yellow-button" @click="$emit('edit', row)">
                <i class="fa-solid fa-edit"></i>
                수정
              </a-button>
              <a-button size="small" danger @click="$emit('remove', row)">
                <i class="fa-solid fa-trash"></i>
                삭제
              </a-button>
            </div>
          </div>
        </div>
      </a-spin>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';

/**
 * 광고 상태 (화면 표시용)
 * - 미게시: 게시 여부 꺼짐
 * - 시작 전 / 게시 중 / 종료: 오늘 날짜와 광고 기간 비교
 */
export function getAdStatus(row, today = dayjs().format('YYYY-MM-DD')) {
  if (!row.isFeatured) return { text: '미게시', color: 'orange' };
  if (row.startDate > today) return { text: '시작 전', color: 'blue' };
  if (row.endDate && row.endDate < today) return { text: '종료', color: 'default' };
  return { text: '게시 중', color: 'green' };
}

export default {
  name: 'HospitalAdTable',
  props: {
    rows: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['add', 'edit', 'toggle', 'remove'],
  setup() {
    const statusOf = (row) => getAdStatus(row);
    const formatDate = (value) => (value ? dayjs(value).format('YYYY-MM-DD') : '-');
    return { statusOf, formatDate };
  },
};
</script>

<style scoped lang="scss">
/* BannerTable.vue 스타일 (끌기·미리보기·링크·클릭수 칸 제외) */
.banner-table-container {
  padding: 20px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.table-info {
  .total-count {
    font-size: 14px;
    color: #666;

    strong {
      color: #333;
      font-weight: 600;
    }
  }
}

.table-actions {
  display: flex;
  gap: 8px;
}

.banner-name {
  .name-text {
    font-weight: 500;
    color: #333;
  }
}

/* 병원 지역 (BannerTable .created-date 색·크기) */
.hospital-region {
  display: block;
  font-size: 12px;
  color: #666;
}

.display-period {
  font-size: 12px;

  .period-item {
    display: flex;
    align-items: center;
    margin-bottom: 2px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .period-label {
    color: #666;
    min-width: 30px;
    margin-right: 4px;
  }

  .period-date {
    color: #333;
    font-weight: 500;
  }
}

.display-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  .status-text {
    font-size: 12px;
    font-weight: 500;

    &.active {
      color: #52c41a;
    }

    &.inactive {
      color: #f5222d;
    }
  }
}

.created-date {
  font-size: 12px;
  color: #666;
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

:deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #000 !important;

  &:hover {
    background-color: #e6b82e !important;
    border-color: #e6b82e !important;
  }

  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

.draggable-table-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.table-header-row {
  display: flex;
  background-color: #fafafa;
  border-bottom: 2px solid #f0f0f0;
  font-weight: 600;
  color: #333;
}

.table-row {
  display: flex;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  &:last-child {
    border-bottom: none;
  }
}

.table-cell {
  padding: 12px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #f0f0f0;

  &:last-child {
    border-right: none;
  }
}

/* 칸 너비 — BannerTable 값. 병원명 칸만 남는 폭을 씀, 상태 칸은 클릭수 칸 너비 */
.index-cell { flex: 0 0 60px; }
.name-cell { flex: 1 1 200px; justify-content: flex-start; }
.period-cell { flex: 0 0 180px; }
.status-cell { flex: 0 0 100px; }
.visible-cell { flex: 0 0 120px; }
.date-cell { flex: 0 0 150px; }
.action-cell { flex: 0 0 170px; }

.loading-container,
.no-data {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  color: #666;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  i {
    font-size: 24px;
    color: #1890ff;
  }
}

/* 좁은 화면 카드 — ParticipantMonitoring.vue .mobile-card-list 값 */
.featured-mobile-list {
  padding: 0;

  .mobile-empty {
    padding: 40px 0;
  }

  .mobile-cards-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .featured-card {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    overflow: hidden;
    transition: all 0.3s ease;

    .card-header {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      background: #fafafa;
      border-bottom: 1px solid #f0f0f0;

      .card-index {
        font-size: 12px;
        color: #999;
        min-width: 24px;
      }

      .card-title {
        font-weight: 500;
        color: #333;
      }

      .card-segment {
        margin-left: auto;
      }
    }

    .card-body {
      padding: 12px;

      .card-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 6px 0;
        border-bottom: 1px solid #f5f5f5;

        &:last-child {
          border-bottom: none;
        }

        .card-label {
          font-size: 12px;
          color: #999;
          flex-shrink: 0;
          width: 60px;
        }

        .card-value {
          font-size: 14px;
          color: #333;
          text-align: right;
          flex: 1;
        }
      }
    }

    .card-footer {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      padding: 10px 12px;
      background: #fafafa;
      border-top: 1px solid #f0f0f0;
    }
  }
}

/* 표 ↔ 카드: 사이드바가 보이는 768~1199px 도 본문 폭이 표(약 940px)보다 좁아 카드로 바꾼다.
   1199px 는 Navbar.vue 태블릿 구간 경계 */
.featured-cards-only {
  display: none;
}

@media screen and (max-width: 1199px) {
  .featured-table-only {
    display: none;
  }

  .featured-cards-only {
    display: block;
  }
}

/* 767px 이하 (App.vue mobileBreakpoint) — BannerModal 768px 규칙과 같은 16px 여백 */
@media screen and (max-width: 767px) {
  .banner-table-container {
    padding: 16px;
  }

  .table-header {
    flex-wrap: wrap;
    gap: 8px;
  }
}
</style>
